'use no memo';

import i18n from 'i18next';
import {DataStatus, Dialog, DialogProps, Separator} from 'platform/components';
import {Box, Show} from 'platform/foundation';
import styled from 'styled-components';

import {useEffect} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isEmpty, split} from 'ramda';

import {TemplateTypeEnum} from '@dms/api/core';
import {testIds} from '@dms/routes';
import {useRenderTemplateExample} from '@dms/settings/shared';
import {EmailWindow} from '@dms/shared-aftersales';

import {getRandomId, Nullish} from 'shared/web';

import {MessageTemplateFormValues} from '../types/MessageTemplateFormValues';

type Props = Omit<DialogProps, 'children'> & {
  templateType: TemplateTypeEnum;
  formApi: UseFormReturn<MessageTemplateFormValues>;
};

const LineWrapper = styled.div`
  white-space: pre;
  min-height: 1em;
`;

export const MessagePreviewDialog = (props: Props) => {
  const context = useWatch({control: props.formApi.control, name: 'context'});

  const {renderSms, renderEmail, smsData, emailData, isLoading, isError} =
    useRenderTemplateExample(context);

  const renderMultiLine = (value: string | Nullish) => {
    if (isNil(value) || isEmpty(value)) {
      return '';
    }
    return split('\n', value).map((line) => (
      <LineWrapper key={`${getRandomId()}_${value}`}>{line}</LineWrapper>
    ));
  };

  useEffect(
    function renderPreviewOnOpen() {
      if (!props.isOpen) {
        return;
      }
      const {body, subject} = props.formApi.getValues();
      if (props.templateType === 'sms' || props.templateType === 'chat') {
        renderSms?.({body: {template: body}});
      } else {
        renderEmail?.({
          body: {
            body,
            subject,
          },
        });
      }
    },
    [props.isOpen, props.formApi, props.templateType, renderEmail, renderSms]
  );

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={i18n.t('page.messageTemplates.labels.previewDialog')}
      data-testid={testIds.settings.messageTemplates('previewDialog')}
    >
      <DataStatus isLoading={isLoading} isError={isError}>
        <Show when={props.templateType === 'sms'}>
          <Box data-testid={testIds.settings.messageTemplates('previewDialog-smsBody')}>
            {renderMultiLine(smsData?.body)}
          </Box>
        </Show>
        <Show when={props.templateType === 'chat'}>
          <Box data-testid={testIds.settings.messageTemplates('previewDialog-chatBody')}>
            {renderMultiLine(smsData?.body)}
          </Box>
        </Show>
        <Show when={props.templateType === 'email'}>
          <Box data-testid={testIds.settings.messageTemplates('previewDialog-emailSubject')}>
            {renderMultiLine(emailData?.subject)}
          </Box>
          <Separator spacing={3} />
          <Box height="60vh">
            <EmailWindow
              htmlData={emailData?.body ?? null}
              isRerendering={isLoading && !isNil(emailData?.body)}
              data-testid={testIds.settings.messageTemplates('previewDialog-emailBody')}
            />
          </Box>
        </Show>
      </DataStatus>
    </Dialog>
  );
};
